// Sign up / log in / forgot password, one flow (AuthFlow) in three presentations:
//  - phones in a browser: Layout's BottomSheet over the current page, so people keep their place
//    (drag handle; drag, scrim, Escape or Android back close it);
//  - wider screens: a card dialog anchored near the top;
//  - the installed app on phones and tablets: a full-screen welcome screen (pages/Auth.tsx).
// Any CTA opens the dialog via useAuthDialog(); /signup, /login and /forgot-password deep links open
// it over the landing page in a browser.
//
// This module is the part every page needs (the provider, the hooks); the dialog, the flow and the
// forms are in AuthForms.tsx, which loads when the dialog first opens or ahead of time while idle.
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '../lib/session';
import { authConfigQuery } from '../lib/queries';
import { lazyComponent } from '../lib/lazy';

export type AuthMode = 'signup' | 'login' | 'forgot' | 'profile';
/** card: desktop dialog (paper) · sheet: phone bottom sheet (canvas) · screen: the app's welcome (paper). */
export type AuthSurface = 'card' | 'sheet' | 'screen';
export type LegalDoc = 'terms' | 'privacy';

interface Ctx {
  open: (mode: AuthMode, next?: string) => void;
}
const AuthCtx = createContext<Ctx>({ open: () => {} });
export const useAuthDialog = () => useContext(AuthCtx);

/** Only same-site paths, so ?next= can't send people to another origin. */
export function safeNext(v: string | null | undefined): string {
  return v && v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/\\') ? v : '/earn';
}

const AUTH_PATHS = ['/signup', '/login', '/forgot-password'];

/* ------------------------------------------------------------------ Media hooks */

export function useMedia(query: string) {
  const subscribe = useMemo(
    () => (cb: () => void) => {
      const m = window.matchMedia(query);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    [query],
  );
  // The pre-render (no window) matches nothing: an ordinary browser tab, the markup to start from.
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** Launched as the installed app (the Play Store TWA, or a home-screen app on iOS). Same test as
 *  the `standalone:` variant in index.css. */
export function useStandalone() {
  const standalone = useMedia('(display-mode: standalone)');
  return (
    standalone ||
    (typeof navigator !== 'undefined' &&
      (navigator as { standalone?: boolean }).standalone === true)
  );
}

/** The dialog and the forms, loaded on demand. */
const AuthDialog = lazyComponent(() => import('./AuthForms').then((m) => m.AuthDialog));
/** Fetch the dialog's code ahead of time (lib/prefetch.ts: signed-out visitors, while idle). */
export const preloadAuthForms = AuthDialog.preload;

/* ------------------------------------------------------------------ Provider */

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ mode: AuthMode; next?: string | undefined } | null>(null);
  const navigate = useNavigate();
  const loc = useLocation();
  const { data: me } = useMe();
  // Signed out: fetch the Turnstile config early, so the form knows whether the check needs room
  // before it opens and nothing shifts once it does.
  useTurnstileKey(me === null);
  const open = useCallback((mode: AuthMode, next?: string) => setState({ mode, next }), []);
  const close = useCallback(() => {
    setState(null);
    // Deep-link routes have nothing behind them but the landing page; go home on close.
    if (AUTH_PATHS.includes(loc.pathname)) navigate('/', { replace: true });
  }, [loc.pathname, navigate]);
  const done = useCallback(
    (next?: string) => {
      setState(null);
      navigate(safeNext(next));
    },
    [navigate],
  );
  return (
    <AuthCtx.Provider value={{ open }}>
      {children}
      {state && (
        // The dialog's code loads on demand (preloadAuthForms: while idle, for signed-out visitors).
        <Suspense fallback={null}>
          <AuthDialog
            mode={state.mode}
            setMode={(mode) => setState((s) => (s ? { ...s, mode } : s))}
            onClose={close}
            onDone={() => done(state.next)}
          />
        </Suspense>
      )}
    </AuthCtx.Provider>
  );
}

export function useTurnstileKey(enabled = true) {
  const { data } = useQuery({
    ...authConfigQuery<{ turnstileSiteKey: string | null }>(),
    enabled,
  });
  return data?.turnstileSiteKey;
}
